import { useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Syncs table sort & pagination state to URL search params so the browser
 * back button restores the previous view.
 *
 * @param {Object} defaults
 * @param {string} defaults.sortBy    - Default sort column
 * @param {string} defaults.sortDir   - Default sort direction ('asc' | 'desc')
 * @param {number} defaults.page      - Default page number (1)
 * @param {string} [prefix]           - Optional prefix for param names (for pages with multiple tables)
 * @returns {{ sortBy, sortDir, page, setSortBy, setSortDir, setPage, setSort }}
 */
export function useTableState({ sortBy: defaultSortBy = 'name', sortDir: defaultSortDir = 'asc', page: defaultPage = 1 } = {}, prefix = '') {
  const [searchParams, setSearchParams] = useSearchParams();

  // Keep setSearchParams in a ref so our callbacks never change identity.
  // react-router does not guarantee setSearchParams is referentially stable,
  // which can cause useEffect loops when callers list our setters as deps.
  const setParamsRef = useRef(setSearchParams);
  setParamsRef.current = setSearchParams;

  const p = prefix ? `${prefix}_` : '';
  const sortBy = searchParams.get(`${p}sortBy`) || defaultSortBy;
  const sortDir = searchParams.get(`${p}sortDir`) || defaultSortDir;
  const page = Number(searchParams.get(`${p}page`)) || defaultPage;

  const update = useCallback((patch) => {
    setParamsRef.current(prev => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(patch)) {
        if (v == null || v === '') {
          next.delete(k);
        } else {
          next.set(k, String(v));
        }
      }
      return next;
    }, { replace: true });
  }, []);

  const setSortBy = useCallback((col) => {
    update({ [`${p}sortBy`]: col, [`${p}page`]: '1' }); // reset page on sort change
  }, [update, p]);

  const setSortDir = useCallback((dir) => {
    update({ [`${p}sortDir`]: dir, [`${p}page`]: '1' });
  }, [update, p]);

  const setSort = useCallback((col, dir) => {
    update({ [`${p}sortBy`]: col, [`${p}sortDir`]: dir, [`${p}page`]: '1' });
  }, [update, p]);

  const setPage = useCallback((pg) => {
    update({ [`${p}page`]: String(pg) });
  }, [update, p]);

  return { sortBy, sortDir, page, setSortBy, setSortDir, setPage, setSort };
}
